'use client'

import { HTMLAttributes } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import clsx from 'classnames'
import Image from 'next/image'
import { useLocale } from 'use-intl'
import { Text } from '@/shared/ui'
import { useMediaQuery, useVH } from '@/shared/utils'
import { Preloader } from '@/shared/ui/preloader'
import { IMainPageSettings } from '@/interfaces/pages'
import { IPostPreview } from '@/interfaces/analytics'
import { localeMap } from '@/shared/utils/directus'
import { IAd } from '@/interfaces/ads'
import { CurrentTime } from './current-time'
import style from './first-section.module.scss'
import { Comments } from '../comments'

interface FirstSectionProps extends HTMLAttributes<HTMLDivElement> {
  loaded?: boolean
  mainPageSettings: IMainPageSettings | null
  comments: IAd[]
}

const variantsTitle = {
  visible: {
    opacity: 1,
    translateY: 0,
    transition: { duration: 0.4, delay: 0.2 },
  },
  hidden: { opacity: 0, translateY: 30 },
}

export const FirstSection = (props: FirstSectionProps) => {
  const { className, children, loaded, mainPageSettings, comments, ...restProps } = props

  const { scrollYProgress } = useScroll()
  const opacity = useTransform(scrollYProgress, [0, 0.24], [1, 0])
  const transformY = useTransform(scrollYProgress, [0, 0.2], ['translateY(0px)', 'translateY(-200px)'])
  const isSmall = useMediaQuery('screen and (orientation: landscape) and (max-height: 520px)')
  const currentLocale = useLocale()

  useVH()

  return (
    <motion.section
      dir="ltr"
      className={clsx(style.container, className, { [style.loaded]: loaded })}
      style={isSmall ? {} : { opacity, transform: transformY }}
    >
      <Preloader className="" loaded={loaded} />
      {loaded && (
        <motion.h1 className={clsx(style.headline)}>
          <span />
          <CurrentTime locale={localeMap[currentLocale]} className={style.time} />
          {mainPageSettings?.title_line_first && (
            <Text className={style.titleLineFirst} variants={variantsTitle} color="white" type="l1" tagName="span">
              {mainPageSettings.title_line_first}
            </Text>
          )}
          {mainPageSettings?.title_line_second && (
            <Text className={style.titleLineSecond} variants={variantsTitle} color="white" type="l1" tagName="span">
              {mainPageSettings.title_line_second}
            </Text>
          )}
        </motion.h1>
      )}
      <Comments comments={comments} className={style.comments} loaded={loaded} />
      <div className={clsx(style.bird, { [style.loaded]: loaded })}>
        <Image
          className={style.birdDesktop}
          src="/bird.webp"
          alt="БРИКС"
          fill
          priority
          style={{ objectFit: 'cover' }}
        />
        <Image
          className={style.birdTablet}
          src="/bird-1200.webp"
          alt="БРИКС"
          fill
          priority
          style={{ objectFit: 'cover' }}
        />
        <Image
          className={style.birdMobile}
          src="/bird-500.webp"
          alt="БРИКС"
          fill
          priority
          style={{ objectFit: 'cover' }}
        />
      </div>
    </motion.section>
  )
}
